import React from 'react';

/* ============================================================================
   SubscriptionUI — the presentational primitives for the redesigned
   Manage-Subscription modal (940px, plan cards, add-on toggle rows, live
   summary). Split out of SubscriptionManager so the manager keeps its billing
   logic readable instead of drowning in style objects.

   Design tokens come from the approved "Manage Subscription Modal" mock:
   violet #5b47d6 on white, 16px card radius, 1.5px resting borders.
   ========================================================================== */

export const V = {
  violet: '#5b47d6',
  violetDeep: '#4a38c2',
  ink: '#191826',
  body: '#3f3f4c',
  muted: '#6b6b7b',
  faint: '#9494a1',
  line: '#e7e6ee',
  hair: '#eeedf3',
  wash: '#f7f7fb',
  green: '#16a34a',
};

/** Small uppercase section header with an optional right-hand note. */
export function SectionHead({ label, note }: { label: string; note?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: V.faint }}>
        {label}
      </span>
      {note && <span style={{ fontSize: 12.5, fontWeight: 600, color: V.faint }}>{note}</span>}
    </div>
  );
}

/** Rounded pill used for org name / plan / billing cycle in the header. */
export function Chip({ children, tone = 'grey' }: { children: React.ReactNode; tone?: 'violet' | 'grey' }) {
  const violet = tone === 'violet';
  return (
    <span style={{
      fontSize: 12.5, fontWeight: 700, padding: '5px 11px', borderRadius: 999,
      background: violet ? '#f3f1fd' : '#eef0f5', color: violet ? V.violet : '#55555f',
    }}>{children}</span>
  );
}

export interface PlanCardProps {
  name: string;
  price: string;
  suffix: string;
  desc: string;
  feats: string[];
  selected: boolean;
  recommended?: boolean;
  /** Marks the plan the org is on today (distinct from what they've selected). */
  current?: boolean;
  onSelect: () => void;
}

export function PlanCard({
  name, price, suffix, desc, feats, selected, recommended, current, onSelect,
}: PlanCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        height: '100%', cursor: 'pointer', font: 'inherit', position: 'relative',
        display: 'flex', flexDirection: 'column', gap: 12, padding: '22px 20px',
        border: `1.5px solid ${selected ? 'transparent' : V.line}`, borderRadius: 16,
        background: '#fff', textAlign: 'left', color: V.ink,
        transition: 'border-color .15s, box-shadow .15s',
      }}
    >
      {selected && (
        <>
          <span style={{
            position: 'absolute', inset: 0, border: `2px solid ${V.violet}`, borderRadius: 16,
            background: 'rgba(91,71,214,.045)', boxShadow: '0 10px 30px rgba(91,71,214,.18)',
            pointerEvents: 'none',
          }} />
          <span style={{
            position: 'absolute', top: 14, right: 14, width: 24, height: 24, borderRadius: '50%',
            background: V.violet, color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 13, fontWeight: 800,
          }}>✓</span>
        </>
      )}
      {recommended && (
        <span style={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          background: V.violet, color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
          padding: '5px 12px', borderRadius: 999, whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(91,71,214,.28)',
        }}>RECOMMENDED</span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.01em' }}>{name}</span>
        {current && (
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '.06em', color: V.muted,
            background: '#eef0f5', padding: '2px 7px', borderRadius: 5,
          }}>CURRENT</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: 23, fontWeight: 800 }}>{price}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: V.faint }}>{suffix}</span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: V.muted, minHeight: 38 }}>{desc}</div>
      <div style={{ height: 1, background: V.hair, margin: '2px 0' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {feats.map((f) => (
          <div key={f} style={{ display: 'flex', gap: 9, fontSize: 13, lineHeight: 1.45, color: V.body }}>
            <span style={{ color: V.violet, fontWeight: 800, flexShrink: 0 }}>✓</span>{f}
          </div>
        ))}
      </div>
    </button>
  );
}

/** Add-on row with an iOS-style switch, matching the mock. */
export function AddonRow({
  label, desc, price, on, onToggle,
}: { label: string; desc: string; price: string; on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%', font: 'inherit', cursor: 'pointer', position: 'relative',
        display: 'flex', alignItems: 'center', gap: 14, padding: '15px 18px',
        border: `1.5px solid ${on ? 'transparent' : V.line}`, borderRadius: 14,
        background: '#fff', textAlign: 'left', color: V.ink, transition: 'border-color .15s',
      }}
    >
      {on && (
        <span style={{
          position: 'absolute', inset: 0, border: `2px solid ${V.violet}`, borderRadius: 14,
          background: 'rgba(91,71,214,.045)', pointerEvents: 'none',
        }} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 14.5, fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 12.5, color: V.faint }}>{desc}</span>
      </div>
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 12.5, fontWeight: 700, color: V.muted, whiteSpace: 'nowrap' }}>{price}</span>
      <span style={{
        width: 42, height: 24, borderRadius: 999, background: on ? V.violet : '#d7d6e0',
        position: 'relative', flexShrink: 0, transition: 'background .15s',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .15s',
        }} />
      </span>
    </button>
  );
}

/** Segmented control for the billing cycle (monthly / termly / annual). */
export function CycleSwitch<T extends string>({
  value, options, onChange,
}: { value: T; options: Array<{ id: T; label: string; hint?: string }>; onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'inline-flex', background: '#f1f0f6', borderRadius: 12, padding: 4, gap: 2 }}>
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            title={o.hint}
            style={{
              font: 'inherit', cursor: 'pointer', border: 'none', borderRadius: 9,
              padding: '7px 14px', fontSize: 13, fontWeight: 700,
              background: active ? '#fff' : 'transparent',
              color: active ? V.ink : V.muted,
              boxShadow: active ? '0 1px 3px rgba(25,24,38,.12)' : 'none',
              transition: 'background .15s, color .15s',
            }}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

/** The live "Current → New → Change" billing summary block. */
export function SummaryBlock({
  currentLabel, newLabel, change, changeLabel, footnote,
}: {
  currentLabel: string; newLabel: string; change: number; changeLabel: string; footnote: string;
}) {
  return (
    <section style={{
      background: V.wash, border: `1px solid ${V.hair}`, borderRadius: 16,
      padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <Row label="Current" value={currentLabel} muted />
      <Row label="New" value={newLabel} strong />
      <div style={{ height: 1, background: '#e6e5ee' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: V.muted }}>Change</span>
        <span style={{
          fontSize: 15, fontWeight: 800,
          color: change === 0 ? '#a6a6b2' : change < 0 ? V.green : V.ink,
        }}>{change === 0 ? '—' : changeLabel}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
        <span style={{ color: V.violet, fontSize: 14 }}>ⓘ</span>
        <span style={{ fontSize: 12.5, color: V.faint }}>{footnote}</span>
      </div>
    </section>
  );
}

function Row({ label, value, muted, strong }: { label: string; value: string; muted?: boolean; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 14, fontWeight: strong ? 700 : 600, color: muted ? V.muted : V.ink }}>{label}</span>
      <span style={{ fontSize: strong ? 17 : 14, fontWeight: strong ? 800 : 700, color: muted ? V.muted : V.ink }}>{value}</span>
    </div>
  );
}

/** Primary / secondary footer buttons. */
export function PrimaryButton({
  children, onClick, disabled,
}: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        font: 'inherit', fontSize: 14, fontWeight: 800, color: '#fff',
        background: V.violet, border: 'none', borderRadius: 12, padding: '12px 24px',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
        boxShadow: '0 8px 20px -6px rgba(91,71,214,.6)', whiteSpace: 'nowrap',
      }}
    >{children}</button>
  );
}

export function GhostButton({
  children, onClick, disabled,
}: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        font: 'inherit', fontSize: 14, fontWeight: 700, color: '#4b4b58', background: '#fff',
        border: '1.5px solid #e0dfe8', borderRadius: 12, padding: '12px 22px',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, whiteSpace: 'nowrap',
      }}
    >{children}</button>
  );
}
